import {ExternalLinkIcon, Loader2Icon} from "lucide-react";
import {useCallback, useEffect, useMemo, useState} from "react";

import {Badge} from "@components/ui/badge";
import {Button} from "@components/ui/button";
import {Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle} from "@components/ui/sheet";
import {
    fetchVNextWorkbenchDrawerDrilldown,
    type LedgerFilters,
    type WorkbenchDrawerTransactionFilters,
    type WorkbenchDrawerTransactionsData,
} from "@hooks/use-vnext";

interface WorkbenchRevenueDrilldownCell {
    nodeId: string;
    nodeLabel: string;
    monthKey: string;
    monthLabel: string;
    value: number;
    segment?: string;
    contactId?: string;
    sourceAccountId?: string;
}

interface WorkbenchRevenueDrilldownSheetProps {
    workspaceId: string;
    cell: WorkbenchRevenueDrilldownCell | null;
    onClose: () => void;
}

const TRANSACTION_PAGE_SIZE = 50;

function formatCurrency(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) return "-";
    if (Math.abs(value) < 0.005) value = 0;
    return `$${value.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
}

function lastDayOfMonth(monthKey: string): string {
    const [year, month] = monthKey.split("-").map(Number);
    const date = new Date(year, month, 0);
    return `${year}-${String(month).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function buildLedgerFilters(filters: WorkbenchDrawerTransactionFilters): LedgerFilters {
    return {
        page: 1,
        page_size: 100,
        sort: "-date",
        date_from: `${filters.month_key}-01`,
        date_to: lastDayOfMonth(filters.month_key),
        account_id: filters.account_id || undefined,
        contact_id: filters.contact_id || undefined,
        segment: filters.segment || undefined,
    };
}

export function WorkbenchRevenueDrilldownSheet({
    workspaceId,
    cell,
    onClose,
}: WorkbenchRevenueDrilldownSheetProps) {
    const [transactionsData, setTransactionsData] = useState<WorkbenchDrawerTransactionsData | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const requestNodeId = cell?.sourceAccountId || cell?.nodeId || "";

    const requestParams = useMemo(() => {
        if (!cell || !requestNodeId) return null;
        return {
            nodeId: requestNodeId,
            monthKey: cell.monthKey,
            level: "transactions" as const,
            contactId: cell.contactId,
            segment: cell.segment,
            limit: TRANSACTION_PAGE_SIZE,
            offset: 0,
        };
    }, [cell, requestNodeId]);

    useEffect(() => {
        if (!requestParams) {
            setTransactionsData(null);
            setError(null);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setError(null);
        fetchVNextWorkbenchDrawerDrilldown(workspaceId, requestParams)
            .then((response) => {
                if (cancelled) return;
                if (response.level !== "transactions") {
                    throw new Error("Unexpected transaction response");
                }
                setTransactionsData(response);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : "Failed to load transactions");
                setTransactionsData(null);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [workspaceId, requestParams]);

    // Tie-out: the cell value is the source of truth and the detail must reconcile
    // to it. Account-level cells reconcile against GL journals (Xero P&L snapshot);
    // customer/segment cells reconcile against ACCREC invoice detail (the same
    // XeroInvoice source that produced the Workbench value). Any gap is surfaced
    // as an explicit residual — read-only, no engine/override math involved.
    const detailSource = transactionsData?.detail_source ?? "journal";
    const isInvoiceDetail = detailSource === "invoice";
    const topLabel = isInvoiceDetail ? "Workbench actual" : "Xero P&L snapshot";
    const detailLabel = isInvoiceDetail ? "Invoice detail" : "GL detail (journals)";
    const unreconciledLabel = isInvoiceDetail ? "Unreconciled to invoices" : "Unreconciled to Xero P&L";

    const snapshotTotal = cell?.value ?? 0;
    const glDetailTotal = transactionsData?.transaction_sum ?? null;
    const unreconciled = glDetailTotal == null ? null : snapshotTotal - glDetailTotal;
    const tieOutMaterial =
        unreconciled != null &&
        Math.abs(unreconciled) >= Math.max(1, Math.abs(snapshotTotal) * 0.005);

    const openInTransactions = useCallback(() => {
        if (!transactionsData) return;
        window.dispatchEvent(
            new CustomEvent("vnext-open-transactions", {
                detail: {filters: buildLedgerFilters(transactionsData.filters_for_transactions)},
            }),
        );
        onClose();
    }, [transactionsData, onClose]);

    return (
        <Sheet open={Boolean(cell)} onOpenChange={(open) => !open && onClose()}>
            <SheetContent
                side="right"
                className="flex w-[390px] max-w-[92vw] flex-col gap-0 p-0 sm:max-w-[390px]"
                data-tour="workbench-transaction-drilldown-drawer"
            >
                <SheetHeader className="border-b px-4 py-4 text-left">
                    <SheetTitle className="text-base font-semibold text-slate-950">{cell?.nodeLabel ?? "Cell Detail"}</SheetTitle>
                    <SheetDescription>{cell ? `${cell.monthLabel} · P&L actual` : ""}</SheetDescription>
                </SheetHeader>

                <div className="flex-1 space-y-4 overflow-y-auto p-4">
                    <div className="rounded-md border p-3">
                        <p className="text-sm font-medium text-slate-900">{cell?.nodeLabel}</p>
                        <p className="mt-1 text-xs text-slate-500">{cell?.monthLabel}</p>
                        <p className="mt-2 text-2xl font-bold tabular-nums text-slate-950">{formatCurrency(snapshotTotal)}</p>
                        <p className="text-[11px] uppercase tracking-wide text-slate-400">{topLabel}</p>

                        {glDetailTotal != null && (
                            <div className="mt-3 space-y-1 border-t pt-2 text-xs">
                                <div className="flex items-center justify-between">
                                    <span className="text-slate-500">{detailLabel}</span>
                                    <span className="tabular-nums text-slate-900">{formatCurrency(glDetailTotal)}</span>
                                </div>
                                <div className="flex items-center justify-between font-medium">
                                    <span className={tieOutMaterial ? "text-rose-600" : "text-slate-500"}>
                                        {unreconciledLabel}
                                    </span>
                                    <span className={`tabular-nums ${tieOutMaterial ? "text-rose-600" : "text-slate-900"}`}>
                                        {formatCurrency(unreconciled)}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-between gap-3">
                        <h4 className="text-xs font-semibold uppercase tracking-[1.6px] text-slate-500">
                            Transactions
                            {transactionsData ? ` (${transactionsData.pagination.total})` : ""}
                        </h4>
                        {transactionsData && (
                            <Badge
                                variant="outline"
                                className={
                                    tieOutMaterial
                                        ? "border-rose-200 bg-rose-50 text-rose-700"
                                        : "border-emerald-200 bg-emerald-50 text-emerald-700"
                                }
                            >
                                {tieOutMaterial ? "Variance" : "Reconciled"}
                            </Badge>
                        )}
                    </div>

                    {isLoading && (
                        <div className="flex items-center justify-center py-8 text-slate-500">
                            <Loader2Icon className="mr-2 size-4 animate-spin" />
                            Loading transactions...
                        </div>
                    )}

                    {error && (
                        <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                            {error}
                        </div>
                    )}

                    {!isLoading && !error && transactionsData?.rows.length === 0 && (
                        <p className="py-4 text-center text-sm text-slate-500">No transactions found for this cell.</p>
                    )}

                    {!isLoading && !error && transactionsData?.rows.length ? (
                        <div className="space-y-1.5">
                            {transactionsData.rows.map((row) => (
                                <div key={row.line_id} className="rounded-md border p-2 text-xs">
                                    <div className="flex items-start justify-between gap-3">
                                        <p className="min-w-0 truncate font-medium text-slate-900">{row.description || "Transaction"}</p>
                                        <p className="shrink-0 font-semibold tabular-nums text-slate-950">{formatCurrency(row.net_amount)}</p>
                                    </div>
                                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-slate-500">
                                        <span>{row.date}</span>
                                        {row.account_code && <span>{row.account_code}</span>}
                                        {row.account_name && <span>{row.account_name}</span>}
                                        {row.source_type && (
                                            <Badge variant="secondary" className="px-1 py-0 text-[10px]">
                                                {row.source_type}
                                            </Badge>
                                        )}
                                    </div>
                                    {row.contact_name && <p className="mt-1 truncate text-[11px] uppercase tracking-wide text-slate-500">{row.contact_name}</p>}
                                </div>
                            ))}
                            {transactionsData.pagination.has_more && (
                                <p className="pt-1 text-center text-xs text-slate-500">
                                    Showing {transactionsData.rows.length} of {transactionsData.pagination.total}
                                </p>
                            )}
                        </div>
                    ) : null}
                </div>

                <div className="border-t p-4">
                    <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        disabled={!transactionsData}
                        onClick={openInTransactions}
                    >
                        <ExternalLinkIcon className="mr-1.5 size-3.5" />
                        Open in Transactions
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
}
